import React, { useState } from 'react';
import { fetchAdminDashboard, fetchEmployeeResponses, submitManagerReview, fetchExitLogs } from '../../services/trainingService';
import '../../styles/training.css';

function formatDualTime(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const london = d.toLocaleString('en-GB', { timeZone: 'Europe/London', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  const beijing = d.toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai', hour: '2-digit', minute: '2-digit', hour12: false });
  return `${london} · 北京 ${beijing}`;
}

const WEEK_TITLES: Record<number, string> = {
  1: 'AML Basics', 2: 'Regulation & Accountability', 3: 'Merchant Onboarding',
  4: 'Transaction Monitoring', 5: 'Refunds & Settlement', 6: 'Sanctions & Cross-Border',
  7: 'Escalation & Reporting', 8: 'Recordkeeping',
};

export default function TrainingAdminPage() {
  const [adminKey, setAdminKey] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [activeTab, setActiveTab] = useState<'progress' | 'exits'>('progress');
  const [branchFilter, setBranchFilter] = useState<number | undefined>();
  const [employees, setEmployees] = useState<any[]>([]);
  const [exitLogs, setExitLogs] = useState<any[]>([]);
  const [totalCards, setTotalCards] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedSession, setSelectedSession] = useState<string | null>(null);
  const [detail, setDetail] = useState<any | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [review, setReview] = useState({ reviewerName: '', overallRating: 'satisfactory', followUpRequired: false, reviewerComments: '' });
  const [reviewSubmitted, setReviewSubmitted] = useState(false);

  const loadDashboard = async (key: string, branch?: number) => {
    setLoading(true);
    setError('');
    try {
      const [data, logs] = await Promise.all([
        fetchAdminDashboard(key, branch),
        fetchExitLogs(key),
      ]);
      setEmployees(data.employees);
      setTotalCards(data.totalCards);
      setExitLogs(logs.logs || []);
      setAuthenticated(true);
    } catch {
      setError('Invalid key or failed to load.');
      setAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const handleAuth = (e: React.FormEvent) => {
    e.preventDefault();
    loadDashboard(adminKey, branchFilter);
  };

  const openDetail = async (sessionId: string) => {
    setSelectedSession(sessionId);
    setDetailLoading(true);
    setReviewSubmitted(false);
    try {
      const data = await fetchEmployeeResponses(sessionId, adminKey);
      setDetail(data);
      setReview({ reviewerName: '', overallRating: 'satisfactory', followUpRequired: false, reviewerComments: '' });
    } catch {
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleReviewSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSession) return;
    await submitManagerReview(selectedSession, adminKey, review);
    setReviewSubmitted(true);
  };

  const grouped = employees.reduce((acc: Record<number, any[]>, emp: any) => {
    const bid = emp.branch_id;
    if (!acc[bid]) acc[bid] = [];
    acc[bid].push(emp);
    return acc;
  }, {});

  if (!authenticated) {
    return (
      <div className="tg-admin tg-admin--auth">
        <div className="tg-admin__auth-card">
          <h1 className="tg-admin__auth-title">AML Training — Admin</h1>
          <form onSubmit={handleAuth}>
            <input
              className="tg-gate__input"
              type="password"
              placeholder="Admin key"
              value={adminKey}
              onChange={e => setAdminKey(e.target.value)}
            />
            <button className="tg-btn tg-btn--primary" type="submit" disabled={loading} style={{ marginTop: 16, width: '100%' }}>
              {loading ? 'Loading…' : 'Access Dashboard'}
            </button>
            {error && <p className="tg-error" style={{ marginTop: 12 }}>{error}</p>}
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="tg-admin">
      <div className="tg-admin__header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
          <h1 className="tg-admin__title">AML Training Dashboard</h1>
          <div className="tg-admin__tabs">
            <button className={`tg-admin__tab ${activeTab === 'progress' ? 'tg-admin__tab--active' : ''}`} onClick={() => setActiveTab('progress')}>Progress</button>
            <button className={`tg-admin__tab ${activeTab === 'exits' ? 'tg-admin__tab--active' : ''}`} onClick={() => setActiveTab('exits')}>Exit Logs {exitLogs.length > 0 && <span className="tg-admin__tab-count">{exitLogs.length}</span>}</button>
          </div>
        </div>
        <div className="tg-admin__filters" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select
            className="tg-gate__input tg-gate__select"
            value={branchFilter || ''}
            onChange={e => {
              const v = e.target.value ? Number(e.target.value) : undefined;
              setBranchFilter(v);
              loadDashboard(adminKey, v);
            }}
          >
            <option value="">All branches</option>
            {Array.from({ length: 10 }, (_, i) => (
              <option key={i + 1} value={i + 1}>Branch {i + 1}</option>
            ))}
          </select>
          <button
            className="tg-admin__view-btn"
            onClick={() => loadDashboard(adminKey, branchFilter)}
            disabled={loading}
            style={{ whiteSpace: 'nowrap' }}
          >
            {loading ? '…' : '↻ Refresh'}
          </button>
        </div>
      </div>

      <div className="tg-admin__body">
        {activeTab === 'exits' && (
          <div className="tg-admin__table-wrap">
            <table className="tg-admin__table">
              <thead>
                <tr>
                  <th>Name (entered)</th>
                  <th>Ref</th>
                  <th>Email</th>
                  <th>Dept</th>
                  <th>Branch</th>
                  <th>Progress</th>
                  <th>MCQ</th>
                  <th>Login (London · Beijing)</th>
                  <th>Exit (London · Beijing)</th>
                </tr>
              </thead>
              <tbody>
                {exitLogs.map((log: any) => (
                  <tr key={log.id}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{log.name_entered || '—'}</div>
                      {log.name_registered && log.name_registered !== log.name_entered && (
                        <div style={{ fontSize: 11, color: '#999' }}>reg: {log.name_registered}</div>
                      )}
                    </td>
                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{log.employee_ref}</td>
                    <td style={{ fontSize: 12 }}>{log.email || '—'}</td>
                    <td>{log.department || '—'}</td>
                    <td>{log.branch_name || log.branch_id}</td>
                    <td>
                      <div className="tg-admin__prog-wrap">
                        <div className="tg-admin__prog-bar" style={{ width: `${Math.min(log.progress_pct || 0, 100)}%`, maxWidth: 60 }} />
                        <span>{log.progress_pct || 0}% · W{log.current_week}</span>
                      </div>
                    </td>
                    <td>{log.mcq_correct}/{log.mcq_answered}</td>
                    <td style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{formatDualTime(log.logged_in_at)}</td>
                    <td style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{formatDualTime(log.exited_at)}</td>
                  </tr>
                ))}
                {exitLogs.length === 0 && (
                  <tr><td colSpan={9} style={{ textAlign: 'center', color: '#999', padding: 32 }}>No exit logs yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        <div className={`tg-admin__table-wrap ${selectedSession ? 'tg-admin__table-wrap--narrow' : ''}`} style={{ display: activeTab === 'progress' ? undefined : 'none' }}>
          {Object.keys(grouped).sort((a, b) => Number(a) - Number(b)).map(bid => (
            <div key={bid} className="tg-admin__branch-group">
              <div className="tg-admin__branch-header">Branch {bid}</div>
              <table className="tg-admin__table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Ref</th>
                    <th>Dept</th>
                    <th>Progress</th>
                    <th>MCQ</th>
                    <th>Status</th>
                    <th>Review</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {grouped[Number(bid)].map((emp: any) => (
                    <tr
                      key={emp.employee_id}
                      className={selectedSession === emp.session_id ? 'tg-admin__row--active' : ''}
                    >
                      <td>{emp.full_name}</td>
                      <td>{emp.employee_ref}</td>
                      <td>{emp.department || '—'}</td>
                      <td>
                        <div className="tg-admin__prog-wrap">
                          <div
                            className="tg-admin__prog-bar"
                            style={{ width: `${Math.min(emp.progress_pct || 0, 100)}%`, maxWidth: 60 }}
                          />
                          <span style={{ whiteSpace: 'nowrap' }}>
                            {emp.current_card_index || 0} / {totalCards}
                          </span>
                        </div>
                      </td>
                      <td>{emp.mcq_correct || 0}/{emp.mcq_answered || 0}</td>
                      <td>
                        <span className={`tg-admin__status tg-admin__status--${emp.status || 'not_started'}`}>
                          {emp.status === 'completed' ? 'Complete' : emp.status === 'in_progress' ? 'In progress' : 'Not started'}
                        </span>
                      </td>
                      <td>{Number(emp.has_review) > 0 ? '✓' : '—'}</td>
                      <td>
                        {emp.session_id && (
                          <button
                            className="tg-admin__view-btn"
                            onClick={() => openDetail(emp.session_id)}
                          >
                            View
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>

        {selectedSession && (
          <div className="tg-admin__detail">
            <button className="tg-admin__close" onClick={() => setSelectedSession(null)}>✕</button>
            {detailLoading && <div className="tg-spinner" />}
            {detail && !detailLoading && (
              <>
                <div className="tg-admin__detail-header">
                  <h2>{detail.employee?.full_name}</h2>
                  <p>{detail.employee?.branch_name} · {detail.employee?.department || 'No dept'} · {detail.employee?.employee_ref}</p>
                  <p>Started: {detail.employee?.started_at ? new Date(detail.employee.started_at).toLocaleDateString() : '—'}</p>
                  {detail.employee?.completed_at && <p>Completed: {new Date(detail.employee.completed_at).toLocaleDateString()}</p>}
                </div>

                {[1, 2, 3, 4, 5, 6, 7, 8].map(w => {
                  const weekResponses = detail.responses.filter((r: any) => r.week_number === w);
                  if (weekResponses.length === 0) return null;
                  return (
                    <div key={w} className="tg-admin__week-section">
                      <h3>Week {w}: {WEEK_TITLES[w]}</h3>
                      {weekResponses.map((r: any) => (
                        <div key={r.question_id} className={`tg-admin__response tg-admin__response--${r.card_type}`}>
                          {r.card_type === 'mcq' ? (
                            <>
                              <span className="tg-admin__resp-type">MCQ</span>
                              <span className={`tg-admin__resp-result ${r.is_correct ? 'tg-admin__resp-result--correct' : 'tg-admin__resp-result--wrong'}`}>
                                Option {['A','B','C','D'][r.selected_option_index]} — {r.is_correct ? 'Correct' : 'Incorrect'}
                              </span>
                            </>
                          ) : (
                            <>
                              <span className="tg-admin__resp-type">
                                {r.card_type === 'required' ? 'Knowledge' : r.card_type === 'open_ended' ? 'Open-ended' : 'Reflection'}
                              </span>
                              <p className="tg-admin__resp-text">{r.response_text}</p>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })}

                {detail.review ? (
                  <div className="tg-admin__review-existing">
                    <h3>Manager Review</h3>
                    <p><strong>Rating:</strong> {detail.review.overall_rating}</p>
                    <p><strong>Follow-up:</strong> {detail.review.follow_up_required ? 'Yes' : 'No'}</p>
                    <p><strong>Comments:</strong> {detail.review.reviewer_comments}</p>
                    <p><strong>Reviewed by:</strong> {detail.review.reviewer_name} on {new Date(detail.review.review_date).toLocaleDateString()}</p>
                  </div>
                ) : (
                  <form className="tg-admin__review-form" onSubmit={handleReviewSubmit}>
                    <h3>Add Manager Review</h3>
                    {reviewSubmitted ? (
                      <p className="tg-admin__review-done">Review saved.</p>
                    ) : (
                      <>
                        <input className="tg-gate__input" placeholder="Reviewer name" value={review.reviewerName} onChange={e => setReview(r => ({ ...r, reviewerName: e.target.value }))} />
                        <select className="tg-gate__input tg-gate__select" value={review.overallRating} onChange={e => setReview(r => ({ ...r, overallRating: e.target.value }))}>
                          <option value="satisfactory">Satisfactory</option>
                          <option value="needs_improvement">Needs Improvement</option>
                          <option value="unsatisfactory">Unsatisfactory</option>
                        </select>
                        <label className="tg-admin__checkbox-label">
                          <input type="checkbox" checked={review.followUpRequired} onChange={e => setReview(r => ({ ...r, followUpRequired: e.target.checked }))} />
                          Follow-up required
                        </label>
                        <textarea className="tg-card__textarea" rows={3} placeholder="Comments…" value={review.reviewerComments} onChange={e => setReview(r => ({ ...r, reviewerComments: e.target.value }))} />
                        <button className="tg-btn tg-btn--primary" type="submit">Save Review</button>
                      </>
                    )}
                  </form>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
